// Noyau — évaluation numérique, prédicats, équivalence d'expressions et de RELATIONS.
// Aucun type de nœud en dur : l'opération d'un nœud est lue dans `table.nodes[t].eval`
// (nom d'une PRIMITIVE du noyau : add, mul, neg, div, pow, sqrt, call, eq, ne, lt, le,
// gt, ge, or, and) ; les fonctions (Call) passent par `table.syntax.functions[name].eval`
// (registre FUNCS : exp, ln, sin…) ; les constantes par `table.syntax.constants[name].value`.
//
// Valeurs : rationnel exact `{p, q}` (tant que les entiers restent sûrs) ou flottant.

import { num, Q, isLeaf, symbols } from './ast.js';
import { derive, varOf, limitValue } from './calculus.js';

// ── Valeurs ───────────────────────────────────────────────────────────────
// Rationnels EXACTS : petits → nombres JS (rapide) ; au-delà → BigInt (lot E4 : « 1 ÷ 3 » et « 0,333333333 » ne
// sont plus confondus par une tolérance flottante). Taille plafonnée (BIG_BITS) : au-delà, repli flottant.
const LIM = 2 ** 26; // au-delà, un produit p × q risque de sortir des entiers sûrs
const BIG_BITS = 4096;
// une valeur dont le numérateur n'est pas un nombre fini (dérivée inconnue…) n'est pas exacte : repli flottant (NaN)
const isQ = (v) =>
  typeof v === 'object' &&
  v !== null &&
  'p' in v &&
  (typeof v.p === 'bigint' || (Number.isFinite(v.p) && Number.isFinite(v.q)));
export const toF = (v) => (isQ(v) ? Number(v.p) / Number(v.q) : v);
const small = (a) =>
  typeof a.p === 'number' && typeof a.q === 'number' && Math.abs(a.p) < LIM && a.q < LIM;
const exact2 = (a, b) => isQ(a) && isQ(b);
const fin = (v) => (isQ(v) ? true : Number.isFinite(v));

const big = (x) => BigInt(x);
const bgcd = (a, b) => {
  a = a < 0n ? -a : a;
  b = b < 0n ? -b : b;
  while (b) [a, b] = [b, a % b];
  return a;
};
const bits = (x) => (x < 0n ? -x : x).toString(2).length;
// rationnel BigInt réduit → forme compacte (nombres JS si sûrs)
function bq(p, q) {
  if (q === 0n) return NaN;
  if (q < 0n) {
    p = -p;
    q = -q;
  }
  const g = bgcd(p, q) || 1n;
  p /= g;
  q /= g;
  if (bits(p) > BIG_BITS || bits(q) > BIG_BITS) return Number(p) / Number(q);
  const safe = (x) => x <= BigInt(Number.MAX_SAFE_INTEGER) && x >= -BigInt(Number.MAX_SAFE_INTEGER);
  return safe(p) && safe(q) ? num(Number(p), Number(q)) : { p, q };
}
const BQ = {
  add: (a, b) => bq(big(a.p) * big(b.q) + big(b.p) * big(a.q), big(a.q) * big(b.q)),
  mul: (a, b) => bq(big(a.p) * big(b.p), big(a.q) * big(b.q)),
  div: (a, b) => bq(big(a.p) * big(b.q), big(a.q) * big(b.p)),
};
function qOp(op, a, b) {
  if (small(a) && small(b)) {
    try {
      return Q[op](a, b);
    } catch {
      /* débordement : BigInt */
    }
  }
  return BQ[op](a, b);
}

export const PRIMS = {
  // factorielle d'un entier naturel (exacte jusqu'à 170!) ; degrés → radians
  fact: (a) => {
    const x = toF(a);
    if (!Number.isInteger(x) || x < 0 || x > 170) return NaN;
    let p = 1n;
    for (let k = 2n; k <= BigInt(x); k++) p *= k;
    return bq(p, 1n);
  },
  deg: (a) => (toF(a) * Math.PI) / 180,
  // valeur absolue ; pourcentage (« 25 % » = 25 ÷ 100)
  abs: (a) =>
    isQ(a)
      ? typeof a.p === 'bigint'
        ? bq(a.p < 0n ? -a.p : a.p, a.q)
        : num(Math.abs(a.p), a.q)
      : Math.abs(toF(a)),
  percent: (a) => (isQ(a) && typeof a.p !== 'bigint' ? num(a.p, a.q * 100) : toF(a) / 100),
  // « k parmi n » = coefficient binomial (n ; k), exact
  parmi: (k, n) => FUNCS.binom(n, k),
  add: (a, b) => (exact2(a, b) ? qOp('add', a, b) : toF(a) + toF(b)),
  mul: (a, b) => (exact2(a, b) ? qOp('mul', a, b) : toF(a) * toF(b)),
  neg: (a) => (isQ(a) ? (typeof a.p === 'bigint' ? { p: -a.p, q: a.q } : num(-a.p, a.q)) : -a),
  div: (a, b) => {
    // 0⁺ / 0⁻ (zéro SIGNÉ, flottant, venu d'une limite ou de l'écriture « 0⁺ ») : a ÷ 0⁺ = ±∞ selon les signes
    if (typeof b === 'number' && b === 0 && toF(a) !== 0 && !Number.isNaN(toF(a)))
      return Math.sign(toF(a)) * (Object.is(b, -0) ? -Infinity : Infinity);
    if (toF(b) === 0) return NaN;
    return exact2(a, b) ? qOp('div', a, b) : toF(a) / toF(b);
  },
  pow: (a, b) => {
    // 1ⁿ, (−1)ⁿ, 0ⁿ : exacts pour tout exposant entier (« 1¹⁰⁰ = 1 », « (−1)¹⁰⁰ = 1 »)
    if (isQ(a) && isQ(b) && Number(b.q) === 1 && Number(a.q) === 1 && Math.abs(Number(a.p)) <= 1) {
      const e = Number(b.p),
        x = Number(a.p);
      if (x === 0) return e > 0 ? num(0) : e === 0 ? num(1) : NaN;
      return num(x === -1 && Math.abs(e) % 2 === 1 ? -1 : 1);
    }
    if (isQ(a) && isQ(b) && Number(b.q) === 1 && Math.abs(Number(b.p)) <= 64) {
      const e = Number(b.p);
      if (Number(a.p) === 0 && e < 0) return NaN;
      const size = Math.abs(e) * Math.max(bits(big(a.p)), bits(big(a.q)));
      if (size < BIG_BITS) {
        let P = big(a.p) ** big(Math.abs(e)),
          R = big(a.q) ** big(Math.abs(e));
        return e >= 0 ? bq(P, R) : bq(R, P);
      }
    }
    // racine exacte : a^(k/n) quand a est une puissance n-ième exacte (« 343^(1/3) = 7 », « 0,064^(1/3) = 0,4 »)
    if (
      isQ(a) &&
      isQ(b) &&
      small(a) &&
      small(b) &&
      Number(b.q) > 1 &&
      Number(b.q) <= 12 &&
      Number(a.p) > 0
    ) {
      const n = Number(b.q),
        rp = Math.round(Number(a.p) ** (1 / n)),
        rq = Math.round(Number(a.q) ** (1 / n));
      if (rp ** n === Number(a.p) && rq ** n === Number(a.q))
        return PRIMS.pow(num(rp, rq), num(Number(b.p), 1));
    }
    return Math.pow(toF(a), toF(b));
  },
  sqrt: (a) => {
    if (isQ(a) && small(a) && a.p >= 0) {
      const sp = Math.round(Math.sqrt(a.p)),
        sq = Math.round(Math.sqrt(a.q));
      if (sp * sp === a.p && sq * sq === a.q) return num(sp, sq);
    }
    return Math.sqrt(toF(a));
  },
};

// Registre des fonctions (eval d'une ligne `functions` de pack). Extensible sans toucher au juge.
export const FUNCS = {
  // PGCD / PPCM d'entiers (fonctions exactes)
  gcd: Object.assign(
    (a, b) => {
      const x = Math.abs(toF(a)),
        y = Math.abs(toF(b));
      if (!Number.isInteger(x) || !Number.isInteger(y)) return NaN;
      let u = x,
        v = y;
      while (v) [u, v] = [v, u % v];
      return num(u, 1);
    },
    { exact: true },
  ),
  lcm: Object.assign(
    (a, b) => {
      const x = Math.abs(toF(a)),
        y = Math.abs(toF(b));
      if (!Number.isInteger(x) || !Number.isInteger(y) || !x || !y) return NaN;
      let u = x,
        v = y;
      while (v) [u, v] = [v, u % v];
      return num((x / u) * y, 1);
    },
    { exact: true },
  ),
  exp: Math.exp,
  ln: Math.log,
  log: Math.log10,
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  sqrt: (x) => Math.sqrt(x),
  abs: Math.abs,
  arg: (x) => (x > 0 ? 0 : x < 0 ? Math.PI : NaN),
  arctan: Math.atan,
  arcsin: Math.asin,
  arccos: Math.acos,
  binom: Object.assign(
    (n, k) => {
      // coefficient binomial C(n ; k), EXACT (entiers)
      const N = isQ(n) && Number(n.q) === 1 ? Number(n.p) : NaN,
        K = isQ(k) && Number(k.q) === 1 ? Number(k.p) : NaN;
      if (!Number.isInteger(N) || !Number.isInteger(K) || K < 0 || N < 0 || K > N || N > 60)
        return NaN;
      let c = 1n;
      for (let i = 0; i < K; i++) c = (c * BigInt(N - i)) / BigInt(i + 1);
      return num(Number(c), 1);
    },
    { exact: true },
  ),
};

// Valeur d'une constante de la table : nombre, ou « +inf » (l'infini, écrit ainsi pour rester du JSON)
export const constValue = (c) =>
  !c ? NaN : typeof c.value === 'number' ? c.value : c.value === '+inf' ? Infinity : NaN;

// Comparaisons tolérantes (exactes entre rationnels)
export function close(a, b, tol = 1e-9) {
  if (typeof a === 'number' && typeof b === 'number' && !Number.isFinite(a) && a === b) return true; // même infini
  if (exact2(a, b)) return big(a.p) * big(b.q) === big(b.p) * big(a.q); // deux rationnels : égalité EXACTE
  const x = toF(a),
    y = toF(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) <= tol * Math.max(1, Math.abs(x), Math.abs(y));
}
function cmp(a, b) {
  if (exact2(a, b)) {
    const d = big(a.p) * big(b.q) - big(b.p) * big(a.q);
    return d > 0n ? 1 : d < 0n ? -1 : 0;
  }
  return Math.sign(toF(a) - toF(b));
}
// « a ≈ b » : b est un arrondi juste de a à la précision où b est écrit (2,5 → au dixième, 3 → à l'unité).
function approxHolds(a, b) {
  const x = toF(a),
    y = toF(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  let d = 0;
  if (isQ(b)) {
    let q = big(b.q),
      k2 = 0,
      k5 = 0;
    while (q % 2n === 0n) {
      q /= 2n;
      k2++;
    }
    while (q % 5n === 0n) {
      q /= 5n;
      k5++;
    }
    if (q !== 1n) return close(a, b, 1e-9);
    d = Math.max(k2, k5);
  }
  return Math.abs(x - y) <= 0.5 * 10 ** -d * (1 + 1e-9);
}
const REL = {
  approx: (a, b) => approxHolds(a, b),
  eq: (a, b, tol) => close(a, b, tol),
  ne: (a, b, tol) => !close(a, b, tol),
  lt: (a, b, tol) => cmp(a, b) < 0 && !close(a, b, tol),
  le: (a, b, tol) => cmp(a, b) <= 0 || close(a, b, tol),
  gt: (a, b, tol) => cmp(a, b) > 0 && !close(a, b, tol),
  ge: (a, b, tol) => cmp(a, b) >= 0 || close(a, b, tol),
};

// ── Évaluation ────────────────────────────────────────────────────────────
// evaluate(ast, env, table) → valeur numérique, booléen (relations / connecteurs), ou NaN / undefined.
export function evaluate(n, env, table, tol = 1e-9) {
  switch (n.t) {
    case 'Num':
      return n.big ? bq(BigInt(n.big), 1n) : { p: n.p, q: n.q };
    case 'Sym': {
      if (env && Object.prototype.hasOwnProperty.call(env, n.name)) return env[n.name];
      // vecteur AB⃗ = B − A (points tirés comme des nombres : les égalités vectorielles sont linéaires) ; 0⃗ = 0
      const vp = vectorPoints(n.name, table);
      if (vp)
        return vp.length ? PRIMS.add(env?.[vp[1]] ?? NaN, PRIMS.neg(env?.[vp[0]] ?? NaN)) : num(0);
      return constValue(table.syntax.constants[n.name]);
    }
    case 'Var':
      return NaN;
  }
  const spec = table.nodes[n.t];
  const op = spec?.eval;
  if (!op) return NaN;
  if (op === 'or' || op === 'and') {
    let unknown = false;
    for (const c of n.k) {
      const v = evaluate(c, env, table, tol);
      if (v === undefined) {
        unknown = true;
        continue;
      }
      if (op === 'or' && v === true) return true;
      if (op === 'and' && v === false) return false;
    }
    return unknown ? undefined : op === 'and';
  }
  if (REL[op]) {
    const vs = n.k.map((c) => evaluate(c, env, table, tol));
    if (vs.some((v) => typeof v === 'boolean' || !fin(v) || Number.isNaN(v))) return undefined;
    for (let i = 0; i + 1 < vs.length; i++) if (!REL[op](vs[i], vs[i + 1], tol)) return false;
    return true;
  }
  if (op === 'call') {
    if (table.syntax.functions[n.name]?.eval === 'abstract') {
      // fonction non interprétée (lot E2)
      const g = env?.[FN]?.[n.name];
      if (!g || n.k.length !== 1) return NaN;
      return g(evaluate(n.k[0], env, table, tol));
    }
    // limite : lim(expr ; point) — la variable de l'expression est liée, la valeur est un réel étendu (±∞)
    if (table.syntax.functions[n.name]?.eval === 'limit')
      return limitValue(n.k[0], n.k[1], env, table);
    const f = FUNCS[table.syntax.functions[n.name]?.eval ?? n.name];
    if (!f) return NaN;
    if (f.exact) return f(...n.k.map((c) => evaluate(c, env, table, tol))); // fonction exacte (coefficient binomial)
    const args = n.k.map((c) => toF(evaluate(c, env, table, tol)));
    return f(...args);
  }
  // dérivée (u)′ : dérivée symbolique exacte par rapport à la variable de u, puis évaluée
  if (op === 'derive')
    return evaluate(derive(n.k[0], varOf(n.k[0], table), table), env, table, tol);
  // point d'approche « a⁺ », « a⁻ » (hors d'une limite : la valeur a)
  // (« 0⁺ » est le zéro signé : 7 ÷ 0⁺ = +∞)
  if (op === 'right' || op === 'left') {
    const x = evaluate(n.k[0], env, table, tol);
    return toF(x) === 0 ? (op === 'right' ? 0 : -0) : x;
  }
  const prim = PRIMS[op];
  if (!prim) return NaN;
  if (n.k.length === 1) return prim(evaluate(n.k[0], env, table, tol));
  let acc = evaluate(n.k[0], env, table, tol);
  for (let i = 1; i < n.k.length; i++) acc = prim(acc, evaluate(n.k[i], env, table, tol));
  return acc;
}

// Valeur exacte d'une expression sans lettre (pour le geste « Calculer ») → {p,q} | null
export function exactValue(n, table) {
  const v = evaluate(n, {}, table);
  return isQ(v) && typeof v.p === 'number' ? v : null; // valeur écrivable (entiers sûrs)
}

// ── Prédicats du noyau (K.pred) ───────────────────────────────────────────
// hyps : [{ of: 'a', kind: 'nonzero' | 'positive' | 'negative' }] (kernel.hypotheses du modèle)
export function makePred(table, hyps = []) {
  const hyp = (name, kind) => hyps.some((h) => h.of === name && h.kind === kind);
  const evalType = (n) => table.nodes[n.t]?.eval;
  const numeric = (n) => (isLeaf(n) ? n.t === 'Num' : n.k.every(numeric));
  const sign = (n) => {
    // +1, -1, 0, ou null (inconnu)
    if (n.t === 'Num') return Math.sign(n.p);
    if (n.t === 'Sym') {
      if (hyp(n.name, 'positive')) return 1;
      if (hyp(n.name, 'negative')) return -1;
      const c = constValue(table.syntax.constants[n.name]);
      if (!Number.isNaN(c)) return Math.sign(c);
      return null;
    }
    if (n.t === 'Var') return null;
    if (numeric(n)) {
      const v = toF(evaluate(n, {}, table));
      return Number.isFinite(v) ? Math.sign(v) : null;
    }
    const op = evalType(n);
    if (op === 'neg') {
      const s = sign(n.k[0]);
      return s === null ? null : -s;
    }
    if (op === 'mul' || op === 'div') {
      let s = 1;
      for (const c of n.k) {
        const x = sign(c);
        if (x === null) return null;
        s *= x;
      }
      return s;
    }
    if (op === 'pow') {
      const e = n.k[1];
      if (e.t === 'Num' && e.q === 1 && e.p % 2 === 0 && nonzero(n.k[0])) return 1;
      const s = sign(n.k[0]);
      return s === 1 ? 1 : null;
    }
    if (op === 'call' && table.syntax.functions[n.name]?.eval === 'exp') return 1;
    return null;
  };
  const nonzero = (n) => {
    if (
      n.t === 'Sym' &&
      (hyp(n.name, 'nonzero') || hyp(n.name, 'positive') || hyp(n.name, 'negative'))
    )
      return true;
    const s = sign(n);
    if (s !== null) return s !== 0;
    const op = !isLeaf(n) && evalType(n);
    if (op === 'mul') return n.k.every(nonzero);
    if (op === 'neg') return nonzero(n.k[0]);
    if (op === 'div') return nonzero(n.k[0]);
    if (op === 'pow') return nonzero(n.k[0]);
    return false;
  };
  const P = {
    nonzero,
    positive: (n) => sign(n) === 1,
    negative: (n) => sign(n) === -1,
    integer: (n) => n.t === 'Num' && n.q === 1,
    number: (n) => numeric(n),
    free_of: (n, v) => !symbols(n).has(typeof v === 'string' ? v : v?.name),
    // l'écriture contient une racine carrée
    has_root: (n) => {
      const sq = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'sqrt');
      const has = (m) => !!m && (m.t === sq || (!isLeaf(m) && m.k.some(has)));
      return has(n);
    },
    // n est le carré du nombre b (9 et 3 ; 1/4 et 1/2) : b > 0
    square_of: (n, b) => {
      if (!n || !b || !numeric(n) || !numeric(b)) return false;
      const vn = evaluate(n, {}, table),
        vb = evaluate(b, {}, table);
      return isQ(vn) && isQ(vb) && toF(vb) > 0 && close(vn, PRIMS.mul(vb, vb));
    },
  };
  const pred = (name, ...args) => (P[name] ? !!P[name](...args) : false);
  Object.assign(pred, P);
  return pred;
}

// ── Échantillonnage déterministe ──────────────────────────────────────────
const POOL = [
  [2, 3],
  [-5, 4],
  [7, 5],
  [3, 1],
  [-2, 1],
  [11, 7],
  [-3, 2],
  [5, 2],
  [1, 3],
  [-7, 3],
  [9, 4],
  [4, 3],
  [-1, 2],
  [13, 5],
  [-8, 3],
  [6, 7],
  [17, 6],
  [-9, 5],
  [5, 3],
  [-11, 4],
];
const TPOOL = [
  [3, 11],
  [-19, 7],
  [23, 5],
  [-5, 13],
  [29, 9],
];

// Fonctions abstraites (lot E2) : à l'échantillon i, chaque fonction non interprétée devient un polynôme de degré 3
// à coefficients rationnels tirés du même réservoir — non linéaire, non pair, non impair : f(a + b) ≠ f(a) + f(b),
// f(2x) ≠ 2f(x), f(−x) ≠ ±f(x). Calcul exact en rationnels. Deux fonctions différentes ont des coefficients différents.
export const FN = Symbol('fonctions');
function abstractFns(table, i) {
  const names = Object.entries(table?.syntax?.functions || {})
    .filter(([, f]) => f.eval === 'abstract')
    .map(([n]) => n)
    .sort();
  if (!names.length) return null;
  const out = {};
  names.forEach((name, j) => {
    const c = [0, 1, 2, 3].map((d) => {
      const [p, q] = POOL[(i * 5 + j * 11 + d * 7 + 3) % POOL.length];
      return num(p, q);
    });
    out[name] = (x) => {
      if (!fin(x) || Number.isNaN(toF(x))) return NaN;
      let acc = c[3];
      for (let d = 2; d >= 0; d--) acc = PRIMS.add(PRIMS.mul(acc, x), c[d]);
      return acc;
    };
  });
  return out;
}

function sampleEnv(vars, i, hyps, fixed, table = null) {
  const env = { ...(fixed || {}) };
  const fns = abstractFns(table, i);
  if (fns) env[FN] = fns;
  // lettres entières (sous une factorielle ou « parmi », ou déclarées entières) : des entiers ≥ 2, croissants
  // dans l'ordre alphabétique (« k parmi n » : k < n), pour que n!, (n − k)!, (k − 1)! existent
  const ints = vars.filter(
    (v) =>
      vars.ints?.has(v) ||
      hyps.some((h) => h.of === v && (h.kind === 'natural' || h.kind === 'integer')),
  );
  vars.forEach((v, j) => {
    if (env[v] !== undefined) return;
    if (vars.ints?.has(v)) {
      env[v] = num(2 + 3 * ints.indexOf(v) + (i % 3));
      return;
    }
    let [p, q] = POOL[(i * 7 + j * 3 + i * j) % POOL.length];
    if (hyps.some((h) => h.of === v && h.kind === 'positive')) p = Math.abs(p);
    if (hyps.some((h) => h.of === v && h.kind === 'negative')) p = -Math.abs(p);
    env[v] = num(p, q);
  });
  return env;
}

// « AB⃗ » → ['A', 'B'] ; « 0⃗ » → [] ; sinon null (un vecteur d'une seule lettre, u⃗, est une inconnue ordinaire)
export function vectorPoints(name, table) {
  const m = table?.lex?.vectorMark;
  if (!m || !name.endsWith(m)) return null;
  const body = name.slice(0, -m.length);
  if (body === '0') return [];
  const pts = [...body];
  return pts.length === 2 ? pts : null;
}
// une liste filtrée garde la marque des lettres entières de la liste d'origine
function keepInts(arr, src) {
  if (src.ints) Object.defineProperty(arr, 'ints', { value: src.ints });
  return arr;
}
function freeVars(asts, table, fixed = {}) {
  const s = new Set();
  for (const a of asts) symbols(a, s);
  for (const n of [...s]) {
    const vp = vectorPoints(n, table);
    if (vp) {
      s.delete(n);
      vp.forEach((p) => s.add(p));
    }
  }
  const out = [...s]
    .filter((n) => !(n in fixed) && Number.isNaN(constValue(table.syntax.constants[n])))
    .sort();
  // lettres sous une factorielle ou « parmi » : échantillonnées en entiers (sampleEnv)
  const ints = new Set();
  const walkInt = (n, under) => {
    if (!n) return;
    if (n.t === 'Sym' && under) ints.add(n.name);
    if (isLeaf(n)) return;
    const ev = table?.nodes?.[n.t]?.eval;
    for (const c of n.k) walkInt(c, under || ev === 'fact' || ev === 'parmi');
  };
  for (const a of asts) walkInt(a, false);
  if (ints.size) Object.defineProperty(out, 'ints', { value: ints });
  return out;
}

// ── Équivalence d'expressions ─────────────────────────────────────────────
const defined = (v) => fin(v) && !Number.isNaN(toF(v));
export function equivExpr(a, b, table, { hyps = [], fixed = {}, samples = 8 } = {}) {
  const vars = freeVars([a, b], table, fixed);
  let ok = 0;
  for (let i = 0; i < samples * 3 && ok < samples; i++) {
    const env = sampleEnv(vars, i, hyps, fixed, table);
    const x = evaluate(a, env, table),
      y = evaluate(b, env, table);
    const dx = defined(x),
      dy = defined(y);
    // lot E3 : définie d'un côté, pas de l'autre → les domaines diffèrent (ln(x²) et 2 ln(x) en x < 0)
    if (dx !== dy) return false;
    if (!dx) continue;
    if (!close(x, y, 1e-9)) return false;
    ok++;
  }
  return ok >= Math.min(3, samples);
}

// ── Équivalence de RELATIONS ──────────────────────────────────────────────
// Méthode 1 (rapide, exacte quand c'est polynomial) : « rapport des écarts ».
//   R1 : f ⋈ g, R2 : f' ⋈' g' (relations binaires de même famille). h = f − g (ou g − f pour < ≤,
//   afin de ramener toute inégalité à h > 0 / h ≥ 0). R1 ⇔ R2 si h1 = c·h2 avec c fini, non nul,
//   INDÉPENDANT de l'inconnue cible (sur des échantillons : paramètres fixés, cible variée) et,
//   pour une inégalité, c > 0 partout (même largeur stricte/large exigée).
// Méthode 2 (repli, et pour « ou » / « et » / chaînes) : comparaison des ENSEMBLES DE SOLUTIONS en
//   la cible, paramètres échantillonnés : on compare la vérité des deux relations sur des points
//   témoins = racines de chaque égalité atomique (balayage + dichotomie), valeurs « cible = expr »
//   lues dans les relations et dans la réponse du modèle, et une grille (pour les inégalités).
//   Elle exige au moins un point où l'une des relations est vraie (sinon : pas d'avis → faux).
export function relationParts(n, table) {
  const spec = table.nodes[n.t];
  if (!spec) return null;
  if (spec.relation) return { kind: 'rel', rel: spec.relation, spec };
  if (spec.connective) return { kind: 'conn', conn: spec.connective, spec };
  return null;
}
export const isRelation = (n, table) => !!relationParts(n, table);

function atomsOf(n, table, out = []) {
  const rp = relationParts(n, table);
  if (!rp) return out;
  if (rp.kind === 'conn') {
    n.k.forEach((c) => atomsOf(c, table, out));
    return out;
  }
  for (let i = 0; i + 1 < n.k.length; i++) out.push({ rel: rp.rel, l: n.k[i], r: n.k[i + 1] });
  return out;
}

function family(rel) {
  if (rel === 'eq') return { fam: 'eq', strict: false, s: 1 };
  if (rel === 'ne') return { fam: 'ne', strict: false, s: 1 };
  if (rel === 'gt') return { fam: 'ineq', strict: true, s: 1 };
  if (rel === 'ge') return { fam: 'ineq', strict: false, s: 1 };
  if (rel === 'lt') return { fam: 'ineq', strict: true, s: -1 };
  if (rel === 'le') return { fam: 'ineq', strict: false, s: -1 };
  return null;
}

function hval(atom, fam, env, table) {
  const l = evaluate(atom.l, env, table),
    r = evaluate(atom.r, env, table);
  const d = PRIMS.add(l, PRIMS.neg(r));
  return fam.s < 0 ? PRIMS.neg(d) : d;
}

function ratioTest(r1, r2, ctx) {
  const { table, target, hyps = [], fixed = {} } = ctx;
  const a1 = atomsOf(r1, table),
    a2 = atomsOf(r2, table);
  if (a1.length !== 1 || a2.length !== 1) return null;
  const rp1 = relationParts(r1, table),
    rp2 = relationParts(r2, table);
  if (rp1.kind !== 'rel' || rp2.kind !== 'rel') return null;
  const f1 = family(a1[0].rel),
    f2 = family(a2[0].rel);
  if (!f1 || !f2 || f1.fam !== f2.fam || f1.strict !== f2.strict) return false;
  const all = freeVars([r1, r2], table, fixed);
  const vars = keepInts(
    all.filter((v) => v !== target),
    all,
  );
  const hasT = target && freeVars([r1, r2], table, fixed).includes(target);
  let good = 0;
  for (let i = 0; i < 18 && good < 4; i++) {
    const env = sampleEnv(vars, i, hyps, fixed, table);
    const tv = hasT ? TPOOL.slice(0, 3) : [null];
    let ref = null,
      n = 0,
      bad = false;
    for (const t of tv) {
      const e = t ? { ...env, [target]: num(t[0], t[1]) } : env;
      const h1 = hval(a1[0], f1, e, table),
        h2 = hval(a2[0], f2, e, table);
      const x1 = toF(h1),
        x2 = toF(h2);
      if (!Number.isFinite(x1) || !Number.isFinite(x2)) {
        bad = true;
        break;
      }
      if (x2 === 0 || x1 === 0) {
        if (close(h1, 0) && close(h2, 0)) continue; // racine commune : pas d'information
        if (close(h1, 0) !== close(h2, 0)) return false;
        continue;
      }
      const c = PRIMS.div(h1, h2);
      if (!fin(c) || close(c, 0, 1e-12)) return false;
      if (f1.fam === 'ineq' && toF(c) < 0) return false;
      if (ref === null) ref = c;
      else if (!close(ref, c, 1e-7)) return false; // le rapport dépend de la cible
      n++;
    }
    if (bad) continue;
    if (n > 0) good++;
  }
  return good >= 2 ? true : null;
}

function rootsOf(atom, target, env, table) {
  const out = [];
  const h = (t) => {
    const e = { ...env, [target]: t };
    return toF(evaluate(atom.l, e, table)) - toF(evaluate(atom.r, e, table));
  };
  const A = -30,
    B = 30,
    N = 240;
  const ts = new Array(N + 1),
    hs = new Array(N + 1);
  for (let i = 0; i <= N; i++) {
    ts[i] = A + ((B - A) * i) / N;
    hs[i] = h(ts[i]);
  }
  const pushRoot = (x) => {
    if (!out.some((y) => Math.abs(y - x) < 1e-9 * Math.max(1, Math.abs(x)))) out.push(x);
  };
  for (let i = 1; i <= N; i++) {
    const ph = hs[i - 1],
      ht = hs[i];
    if (!Number.isFinite(ph) || !Number.isFinite(ht)) continue;
    if (ph === 0) pushRoot(ts[i - 1]);
    else if (Math.sign(ph) !== Math.sign(ht) && ht !== 0) {
      let lo = ts[i - 1],
        hi = ts[i],
        hl = ph;
      for (let k = 0; k < 55; k++) {
        const m = (lo + hi) / 2,
          hm = h(m);
        if (!Number.isFinite(hm)) break;
        if (Math.sign(hm) === Math.sign(hl)) {
          lo = m;
          hl = hm;
        } else hi = m;
      }
      pushRoot((lo + hi) / 2);
    }
  }
  // racines doubles (sans changement de signe) : minima locaux de |h| proches de 0
  for (let i = 1; i < N; i++) {
    const a = Math.abs(hs[i - 1]),
      b = Math.abs(hs[i]),
      c = Math.abs(hs[i + 1]);
    if (!(b <= a && b <= c && b < 1) || Math.sign(hs[i - 1]) !== Math.sign(hs[i + 1])) continue;
    let lo = ts[i - 1],
      hi = ts[i + 1];
    for (let k = 0; k < 60; k++) {
      const m1 = lo + (hi - lo) / 3,
        m2 = hi - (hi - lo) / 3;
      if (Math.abs(h(m1)) < Math.abs(h(m2))) hi = m2;
      else lo = m1;
    }
    const x = (lo + hi) / 2;
    if (Math.abs(h(x)) < 1e-10) pushRoot(x);
  }
  return out;
}

function solvedValues(n, target, env, table) {
  const vals = [];
  for (const a of atomsOf(n, table)) {
    for (const [s, o] of [
      [a.l, a.r],
      [a.r, a.l],
    ]) {
      if (s.t === 'Sym' && s.name === target && !symbols(o).has(target)) {
        const v = evaluate(o, env, table);
        if (fin(v) && !Number.isNaN(toF(v))) vals.push(v);
      }
    }
  }
  return vals;
}

function inHyps(v, t, hyps, table) {
  for (const h of hyps) {
    let x;
    if (h.kind === 'domain') {
      if (h.of === v && table && evaluate(h.expr, { [v]: t }, table, 1e-9) !== true) return false;
      continue;
    }
    if (h.of === v) x = toF(t);
    else if (h.expr && table && symbols(h.expr).has(v) && symbols(h.expr).size === 1)
      x = toF(evaluate(h.expr, { [v]: t }, table));
    else continue;
    if (h.kind === 'positive' && !(x > 0)) return false;
    if (h.kind === 'negative' && !(x < 0)) return false;
    if (h.kind === 'nonzero' && x === 0) return false;
  }
  return true;
}
// Une exponentielle presque nulle en ce point (« e^(−x²) » en x = −5 vaut 10⁻¹¹, sous la tolérance du calcul) : la
// vérité d'une égalité n'y est pas sûre (« (4x² − 2)e^(−x²) = 0 » y paraît vraie)
function underflowsAt(r, e, table) {
  let found = false;
  const toNum = (v) =>
    typeof v === 'number' ? v : v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;
  const walk = (n) => {
    if (found || isLeaf(n)) return;
    const op = table.nodes[n.t]?.eval;
    let arg = null;
    if (n.name === 'exp' && n.k.length === 1) arg = n.k[0];
    else if (op === 'pow' && n.k[0].t === 'Sym' && n.k[0].name === 'e') arg = n.k[1];
    if (arg) {
      const a = toNum(evaluate(arg, e, table));
      if (Number.isFinite(a) && a < -12) found = true;
    }
    n.k.forEach(walk);
  };
  walk(r);
  return found;
}

function truthSetTest(r1, r2, ctx) {
  const { table, target, hyps = [], fixed = {}, answer } = ctx;
  if (!target) return false;
  const where = ctx.where ?? []; // conditions écrites par l'élève : seuls ces points comptent
  const dom = ctx.domain === undefined ? r1 : ctx.domain; // par défaut : la première relation (la référence)
  // entiers de la feuille (le k de « x = π ÷ 3 + 2kπ ») : une relation qui en contient est vraie s'il EXISTE un k
  const intVars = freeVars([r1, r2], table, fixed).filter(
    (v) =>
      v !== target &&
      hyps.some((h) => h.of === v && (h.kind === 'integer' || h.kind === 'natural')),
  );
  const all = freeVars([r1, r2], table, fixed);
  const vars = keepInts(
    all.filter((v) => v !== target && !intVars.includes(v)),
    all,
  );
  const atoms = [...atomsOf(r1, table), ...atomsOf(r2, table)];
  const KS = intVars.length === 1 ? Array.from({ length: 25 }, (_, j) => j - 12) : [null];
  const withK = (env, k) => (k === null ? env : { ...env, [intVars[0]]: num(k, 1) });
  const truth = (r, e) => {
    if (!intVars.length || !intVars.some((v) => symbols(r).has(v)))
      return evaluate(r, e, table, 1e-7);
    if (intVars.length > 1) return undefined;
    let seen = false;
    for (const k of KS) {
      const b = evaluate(r, withK(e, k), table, 1e-7);
      if (b === true) return true;
      if (b === false) seen = true;
    }
    return seen ? false : undefined;
  };
  let sawTrue = false,
    compared = 0,
    samplesOk = 0;
  for (let i = 0; i < 6 && samplesOk < 3; i++) {
    const env = sampleEnv(vars, i + 3, hyps, fixed, table);
    const pts = [];
    for (const k of KS) {
      const ek = withK(env, k);
      for (const src of [r1, r2, answer].filter(Boolean))
        pts.push(...solvedValues(src, target, ek, table));
      // de part et d'autre de chaque racine (une solution « n > 17,7 » n'a aucun point dans la grille ci-dessous)
      for (const a of atoms) {
        if (
          k !== null &&
          k !== 0 &&
          !intVars.some((v) => symbols(a.l).has(v) || symbols(a.r).has(v))
        )
          continue;
        for (const x of rootsOf(a, target, ek, table)) {
          const h = 0.37 * Math.max(1, Math.abs(x));
          pts.push(x, x - h, x + h);
        }
      }
    }
    for (let g = -12.37; g < 12.5; g += 0.731) pts.push(g);
    let any = false;
    for (const t of pts) {
      const e = { ...env, [target]: t };
      // domaine d'étude : là où la relation de référence (le départ) est définie, et dans les hypothèses de
      // l'exercice — on résout « ln(x) ≤ 2 » sur ]0 ; +∞[, et « x ≤ e² » y est équivalent
      if (!inHyps(target, t, hyps, table)) continue;
      if (where.some((w) => evaluate(w, e, table, 1e-7) !== true)) continue;
      if (dom && truth(dom, e) === undefined) continue;
      const b1 = truth(r1, e),
        b2 = truth(r2, e);
      // lot E3 : une solution d'un côté où l'autre n'est même pas définie (dans le domaine) → ensembles différents
      if ((b1 === true && b2 === undefined) || (b2 === true && b1 === undefined)) return false;
      if (b1 === undefined || b2 === undefined) continue;
      // vraie seulement parce qu'une exponentielle y vaut 0 en calcul machine : point douteux, on ne le compte pas
      if (b1 !== b2 && underflowsAt(b1 ? r1 : r2, e, table)) continue;
      if (b1 !== b2) return false;
      if (b1) sawTrue = true;
      compared++;
      any = true;
    }
    if (any) samplesOk++;
  }
  return sawTrue && compared > 0;
}

// ctx = { table, target, hyps, fixed: { nom: valeur }, answer?: AST de la réponse du modèle }
export function equivRelation(r1, r2, ctx) {
  const { table } = ctx;
  if (!isRelation(r1, table) || !isRelation(r2, table)) return false;
  const z = integerTest(r1, r2, ctx);
  if (z !== null) return z;
  const x = extendedTest(r1, r2, ctx);
  if (x !== null) return x;
  const r = ratioTest(r1, r2, ctx);
  if (r === true) return true;
  return truthSetTest(r1, r2, ctx);
}

// ── Inconnue entière (kernel.hypotheses : natural / integer) ──────────────
// Deux relations en la seule inconnue n sont équivalentes si elles ont les mêmes solutions ENTIÈRES (« n > 10,3 »
// et « n ≥ 11 ») : on les compare en des entiers bien choisis. null = pas une inconnue entière.
function integerTest(r1, r2, ctx) {
  const { table, target, hyps = [], fixed = {} } = ctx;
  if (!target) return null;
  const kind = hyps.find(
    (h) => h.of === target && (h.kind === 'natural' || h.kind === 'integer'),
  )?.kind;
  if (!kind) return null;
  const vars = freeVars([r1, r2], table, fixed);
  if (vars.some((v) => v !== target)) return null;
  // entiers testés : 0..300 (et leurs opposés), autour de chaque valeur numérique des membres (là où la vérité
  // d'une relation peut changer), et quelques grands entiers
  const ks = new Set();
  for (let k = 0; k <= 300; k++) {
    ks.add(k);
    if (kind === 'integer') ks.add(-k);
  }
  for (const r of [r1, r2])
    for (const m of r.k) {
      if (symbols(m).has(target)) continue;
      const v = evaluate(m, { ...fixed }, table);
      const x =
        typeof v === 'number' ? v : v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;
      if (!Number.isFinite(x) || Math.abs(x) > 1e9) continue;
      for (let d = -2; d <= 2; d++) ks.add(Math.floor(x) + d);
    }
  for (const k of [500, 1000, 5000, 10000, 100000]) {
    ks.add(k);
    if (kind === 'integer') ks.add(-k);
  }
  let compared = 0;
  for (const k of ks) {
    if (kind === 'natural' && k < 0) continue;
    const env = { ...fixed, [target]: num(k) };
    const a = evaluate(r1, env, table),
      b = evaluate(r2, env, table);
    // un très grand entier peut dépasser les flottants (1,05^100000) : on ne le compte pas
    if (typeof a !== 'boolean' || typeof b !== 'boolean') continue;
    if (a !== b) return false;
    compared++;
  }
  return compared >= 20 ? true : null;
}

// ── Limites : « L = lim(…) », « L = +∞ » ────────────────────────────────────
// Une égalité « cible = expression » dont l'expression contient une limite ou l'infini se juge par sa VALEUR
// étendue (aucune lettre libre hors des limites) : deux lignes sont équivalentes quand elles donnent la même
// limite. Une forme indéterminée (+∞ − ∞) n'a pas de valeur : elle n'est équivalente à rien.
export function hasExtended(n, table) {
  if (n.t === 'Sym') return constValue(table.syntax.constants[n.name]) === Infinity;
  if (isLeaf(n)) return false;
  if (n.t === 'Call' && table.syntax.functions[n.name]?.eval === 'limit') return true;
  return n.k.some((c) => hasExtended(c, table));
}
export function extendedOf(rel, ctx) {
  const { table, target, fixed = {} } = ctx;
  const rp = relationParts(rel, table);
  if (!rp || rp.rel !== 'eq' || rel.k.length !== 2) return null;
  const [l, r] = rel.k;
  if (!(l.t === 'Sym' && l.name === target) || !hasExtended(r, table)) return null;
  return evaluate(r, { ...fixed }, table);
}
function extendedTest(r1, r2, ctx) {
  const e1 = extendedOf(r1, ctx),
    e2 = extendedOf(r2, ctx);
  if (e1 === null && e2 === null) return null;
  const val = (rel, e) => {
    if (e !== null) return e;
    const rp = relationParts(rel, ctx.table);
    if (
      !rp ||
      rp.rel !== 'eq' ||
      rel.k.length !== 2 ||
      !(rel.k[0].t === 'Sym' && rel.k[0].name === ctx.target)
    )
      return NaN;
    return evaluate(rel.k[1], { ...(ctx.fixed || {}) }, ctx.table);
  };
  const a = val(r1, e1),
    b = val(r2, e2);
  const ok = (v) => (isQ(v) || typeof v === 'number') && !Number.isNaN(toF(v));
  if (!ok(a) || !ok(b)) return false;
  return close(a, b, 1e-9);
}

// Vérité d'une relation sous des valeurs connues (feuille « formule »), les autres lettres échantillonnées.
export function holds(rel, table, { fixed = {}, hyps = [] } = {}) {
  const vars = freeVars([rel], table, fixed);
  let ok = 0;
  for (let i = 0; i < 12 && ok < 4; i++) {
    const v = evaluate(rel, sampleEnv(vars, i, hyps, fixed, table), table);
    if (v === undefined) continue;
    if (!v) return false;
    ok++;
  }
  return ok > 0;
}
